/**
 * Page Structure store for managing page structure data
 * 
 * This store handles caching and syncing of page structure data for users,
 * using the GenericStore pattern for persistent caching.
 */

import { storeRegistry } from "@/rerun-manager/GenericStore";
import { apiService } from "@/lib/api-functions/api-service";

// Page structure interface matching the API response
export interface PageStructure {
  id: string;
  user_id: string;
  home: Record<string, unknown> | null;
  text_management: Record<string, unknown> | null;
  documents: Record<string, unknown> | null;
  glossary: Record<string, unknown> | null;
  blueprints: Record<string, unknown> | null;
  typesandtags?: Record<string, unknown> | null;
  settings?: Record<string, unknown> | null;
  tms_page?: Record<string, unknown> | null;
  library?: Record<string, unknown> | null;
  updated_at: string;
}

/**
 * Store for page structures, using GenericStore
 */
export const pageStructureStore = storeRegistry.getStore<PageStructure>({
  key: 'page_structures',
  ttlMs: 1000 * 60 * 30, // 30 minutes cache
});

/**
 * Fetch page structures for a specific user from the API and sync the store
 * @param userId - The user ID to fetch page structures for
 * @param toast - Optional toast function for error reporting
 */
export async function fetchPageStructures(
  userId: string,
  toast?: (props: { title?: React.ReactNode; description?: React.ReactNode; variant?: string }) => void
): Promise<PageStructure[]> {
  try {
    // Get the auth token
    const token = await apiService.getToken();
    if (!token) {
      throw new Error('No authentication token available');
    }

    // Fetch data from API
    const result = await apiService.fetchPageStructures();
    if (!result.success || !result.data) {
      throw new Error(result.error || 'Failed to fetch page structures');
    }

    const pageStructures = result.data;

    // Sync the store with the fetched data
    await pageStructureStore.syncFromRemote(async () => pageStructures);

    return pageStructures;
  } catch (err) {
    const message = err instanceof Error ? err.message : 'Failed to fetch page structures';
    if (toast) {
      toast({
        title: 'Error',
        description: message,
        variant: 'destructive',
      });
    }
    throw new Error(message);
  }
}

/**
 * Fetch a specific page structure by ID
 * @param id - The page structure ID to fetch
 * @param toast - Optional toast function for error reporting
 */
export async function fetchPageStructureById(
  id: string,
  toast?: (props: { title?: React.ReactNode; description?: React.ReactNode; variant?: string }) => void
): Promise<PageStructure | null> {
  try {
    // Get the auth token
    const token = await apiService.getToken();
    if (!token) {
      throw new Error('No authentication token available');
    }

    // Fetch data from API
    const result = await apiService.fetchPageStructureById(id);
    if (!result.success || !result.data) {
      throw new Error(result.error || 'Failed to fetch page structure');
    }

    const pageStructure = result.data;

    // Update the store with the fetched data (create new array to avoid mutation)
    const currentData = pageStructureStore.getData();
    const existingIndex = currentData.findIndex(item => item.id === id);
    
    const updatedData = existingIndex >= 0
      ? currentData.map((item, idx) => idx === existingIndex ? pageStructure : item)
      : [...currentData, pageStructure];
    
    pageStructureStore.setData(updatedData);

    return pageStructure;
  } catch (err) {
    const message = err instanceof Error ? err.message : 'Failed to fetch page structure';
    if (toast) {
      toast({
        title: 'Error',
        description: message,
        variant: 'destructive',
      });
    }
    throw new Error(message);
  }
}

/**
 * Create a new page structure
 * @param pageStructure - The page structure data to create
 * @param toast - Optional toast function for error reporting
 */
export async function createPageStructure(
  pageStructure: Omit<PageStructure, 'id' | 'user_id' | 'updated_at'>,
  toast?: (props: { title?: React.ReactNode; description?: React.ReactNode; variant?: string }) => void
): Promise<PageStructure> {
  try {
    // Get the auth token
    const token = await apiService.getToken();
    if (!token) {
      throw new Error('No authentication token available');
    }

    // Create page structure via API
    const result = await apiService.createPageStructure(pageStructure);
    if (!result.success || !result.data) {
      throw new Error(result.error || 'Failed to create page structure');
    }

    const newPageStructure = result.data;

    // Add to store (create new array to avoid mutation)
    const currentData = pageStructureStore.getData();
    pageStructureStore.setData([...currentData, newPageStructure]);

    return newPageStructure;
  } catch (err) {
    const message = err instanceof Error ? err.message : 'Failed to create page structure';
    if (toast) {
      toast({
        title: 'Error',
        description: message,
        variant: 'destructive',
      });
    }
    throw new Error(message);
  }
}

/**
 * Update an existing page structure
 * @param id - The page structure ID to update
 * @param pageStructure - The updated page structure data
 * @param toast - Optional toast function for error reporting
 */
export async function updatePageStructure(
  id: string,
  pageStructure: Partial<Omit<PageStructure, 'id' | 'user_id' | 'updated_at'>>,
  toast?: (props: { title?: React.ReactNode; description?: React.ReactNode; variant?: string }) => void
): Promise<PageStructure> {
  try {
    // Get the auth token
    const token = await apiService.getToken();
    if (!token) {
      throw new Error('No authentication token available');
    }
    // Update page structure via API
    const result = await apiService.updatePageStructure(id, pageStructure);
    if (!result.success || !result.data) {
      throw new Error(result.error || 'Failed to update page structure');
    }

    const updatedPageStructure = result.data;

    // Update in store (create new array to avoid mutation)
    const currentData = pageStructureStore.getData();
    const existingIndex = currentData.findIndex(item => item.id === id);
    
    if (existingIndex >= 0) {
      const updatedData = currentData.map((item, idx) => 
        idx === existingIndex ? updatedPageStructure : item
      );
      pageStructureStore.setData(updatedData);
    }

    return updatedPageStructure;
  } catch (err) {
    const message = err instanceof Error ? err.message : 'Failed to update page structure';
    if (toast) {
      toast({
        title: 'Error',
        description: message,
        variant: 'destructive',
      });
    }
    throw new Error(message);
  }
}

/**
 * Update a specific section of a page structure (e.g., text_management, home, etc.)
 * This is more efficient than updating the entire page structure
 * @param id - The page structure ID to update
 * @param section - The section to update (e.g., 'text_management', 'home', 'glossary', 'blueprints')
 * @param sectionData - The updated section data
 * @param toast - Optional toast function for error reporting
 */
export async function updatePageStructureSection(
  id: string,
  section: 'home' | 'text_management' | 'documents' | 'glossary' | 'blueprints' | 'typesandtags' | 'settings' | 'tms_page' | 'library',
  sectionData: Record<string, unknown> | null,
  toast?: (props: { title?: React.ReactNode; description?: React.ReactNode; variant?: string }) => void
): Promise<PageStructure> {
  try {
    // Get the auth token
    const token = await apiService.getToken();
    if (!token) {
      throw new Error('No authentication token available');
    }

    // Prepare the update object with only the specific section
    const updateData: Partial<Omit<PageStructure, 'id' | 'user_id' | 'updated_at'>> = {};
    updateData[section] = sectionData;


    // Update page structure via API
    const result = await apiService.updatePageStructure(id, updateData);
    if (!result.success || !result.data) {
      throw new Error(result.error || `Failed to update ${section} section`);
    }

    const updatedPageStructure = result.data;

    // Update in store (create new array to avoid mutation)
    const currentData = pageStructureStore.getData();
    const existingIndex = currentData.findIndex(item => item.id === id);
    
    if (existingIndex >= 0) {
      const updatedData = currentData.map((item, idx) => 
        idx === existingIndex ? updatedPageStructure : item
      );
      pageStructureStore.setData(updatedData);
    }

    return updatedPageStructure;
  } catch (err) {
    const message = err instanceof Error ? err.message : `Failed to update ${section} section`;
    if (toast) {
      toast({
        title: 'Error',
        description: message,
        variant: 'destructive',
      });
    }
    throw new Error(message);
  }
}

/**
 * Update a specific row within a section of a page structure
 * This is the most efficient way to update individual rows
 * @param id - The page structure ID to update
 * @param section - The section to update (e.g., 'text_management', 'home', 'glossary', 'blueprints')
 * @param rowId - The ID of the row to update
 * @param rowData - The updated row data
 * @param toast - Optional toast function for error reporting
 */
export async function updatePageStructureRow(
  id: string,
  section: 'home' | 'text_management' | 'documents' | 'glossary' | 'blueprints' | 'typesandtags' | 'settings' | 'tms_page' | 'library',
  rowId: string,
  rowData: Record<string, unknown>,
  toast?: (props: { title?: React.ReactNode; description?: React.ReactNode; variant?: string }) => void
): Promise<PageStructure> {
  try {
    // Get the current page structure from cache first
    const currentStructure = getCachedPageStructure(id);
    if (!currentStructure) {
      throw new Error('Page structure not found in cache'); 
    }

    // Get the current section data
    const currentSectionData = currentStructure[section] as Record<string, unknown> | null;
    if (!currentSectionData || typeof currentSectionData !== 'object') {
      throw new Error(`Invalid ${section} section data`);
    }

    // Ensure the section has a rows array
    if (!Array.isArray(currentSectionData.rows)) {
      throw new Error(`${section} section does not have a rows array`);
    }

    // Find and update the specific row
    const updatedRows = currentSectionData.rows.map((row: Record<string, unknown>) => 
      (row.id as string) === rowId ? { ...row, ...rowData } : row
    );

    // Check if the row was found and updated
    const rowWasUpdated = updatedRows.some((row: Record<string, unknown>) => (row.id as string) === rowId);
    if (!rowWasUpdated) {
      throw new Error(`Row with ID ${rowId} not found in ${section} section`);
    }

    // Create the updated section data
    const updatedSectionData = {
      ...currentSectionData,
      rows: updatedRows
    };

    // Update the section
    return await updatePageStructureSection(id, section, updatedSectionData, toast);
  } catch (err) {
    const message = err instanceof Error ? err.message : `Failed to update row ${rowId} in ${section} section`;
    if (toast) {
      toast({
        title: 'Error',
        description: message,
        variant: 'destructive',
      });
    }
    throw new Error(message);
  }
}

/**
 * Get cached page structures for a specific user
 * @param userId - The user ID to get page structures for
 */
export function getCachedPageStructures(userId: string): PageStructure[] {
  const allStructures = pageStructureStore.getData();
  return allStructures.filter(structure => structure.user_id === userId);
}

/**
 * Get cached page structure by ID
 * @param id - The page structure ID to get
 */
export function getCachedPageStructure(id: string): PageStructure | null {
  const allStructures = pageStructureStore.getData();
  return allStructures.find(structure => structure.id === id) || null;
}

/**
 * Clear page structure cache
 */
export async function clearPageStructureCache() {
  try {
    await pageStructureStore.clearCache();
  } catch (error) {
    console.error('Failed to clear page structure cache:', error);
  }
} 